/* Ancestral Brotherhood — the programs.
 *
 * Read the program, do it, mark it done. That is the whole feature.
 *
 * Every program is already in the HTML, server-rendered from the
 * "Training program" metaobjects. This file only shows and hides, and
 * remembers which days a man has finished. If it never loads, the page is
 * still a complete, readable set of programs.
 *
 * STORAGE. localStorage holds the record always — it is the fast copy, and
 * it is the only copy while signed out. When a customer IS signed in, the
 * same record is mirrored to their Shopify account through the app proxy at
 * /apps/ancestral/progress, so it follows them to their phone.
 *
 * WHY THE LOCAL COPY IS STILL AUTHORITATIVE ON SCREEN. The network round
 * trip is slow and can fail. Marking a day done paints immediately from
 * local state and queues the save; if the save never lands, the man has lost
 * nothing on the device he is standing on.
 *
 * MERGE RULE. Account and device can both hold days the other does not
 * (marked a day on his phone with no signal, then opened his laptop). We
 * take the union, and where both hold the same day we keep the EARLIER
 * date — that is when the work actually happened.
 *
 * Shape: { "<program-handle>": { "<day index>": "<ISO date>" } }
 */
(() => {
  "use strict";

  const KEY  = "ab-progress-v1";
  const root = document.querySelector("[data-abp]");
  if (!root) return;

  const CUSTOMER = root.dataset.abpCustomer || "";
  const LOGIN    = root.dataset.abpLogin || "/account/login";
  const ENDPOINT = "/apps/ancestral/progress";

  /* -------------------------------------------------------------- store */
  const load = () => {
    try { return JSON.parse(localStorage.getItem(KEY) || "{}") || {}; }
    catch (e) { return {}; }
  };
  const save = (data) => {
    try { localStorage.setItem(KEY, JSON.stringify(data)); }
    catch (e) { /* private mode — the page still works, it just forgets */ }
    if (window.ABProgress && typeof window.ABProgress.onchange === "function") {
      window.ABProgress.onchange(data);
    }
    push();
  };

  let state = load();

  window.ABProgress = {
    get: () => JSON.parse(JSON.stringify(state)),
    set: (data) => { state = data || {}; save(state); paintAll(); },
    onchange: null
  };

  /* --------------------------------------------------------------- bits */
  const q  = (s, c) => (c || root).querySelector(s);
  const qa = (s, c) => Array.from((c || root).querySelectorAll(s));
  const grid    = q("[data-abp-grid]");
  const detail  = q("[data-abp-detail]");
  const tally   = q("[data-abp-tally]");
  const record  = q("[data-abp-record]");
  const recList = q("[data-abp-record-list]");
  const syncEl  = q("[data-abp-sync]");

  const fmt = (iso) => {
    const d = new Date(iso);
    if (isNaN(d)) return "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const doneCount = (handle) => Object.keys(state[handle] || {}).length;

  /* Scoped to the grid on purpose. The record rows below also carry
     data-abp-card so a tap on one opens that program — an unscoped query
     would count those rows as programs on the next repaint and duplicate
     every line in the record. */
  const cards = () => qa("[data-abp-grid] [data-abp-card]");

  /* ---------------------------------------------------------- account -- */

  /* Union of two records; on a collision the earlier date wins. */
  function merge(a, b) {
    const out = {};
    const handles = new Set(Object.keys(a || {}).concat(Object.keys(b || {})));
    handles.forEach((h) => {
      const left = (a && a[h]) || {};
      const right = (b && b[h]) || {};
      const days = {};
      new Set(Object.keys(left).concat(Object.keys(right))).forEach((d) => {
        const l = left[d], r = right[d];
        if (l && r) days[d] = (Date.parse(l) <= Date.parse(r)) ? l : r;
        else days[d] = l || r;
      });
      if (Object.keys(days).length) out[h] = days;
    });
    return out;
  }

  function status(text, tone) {
    if (!syncEl) return;
    syncEl.hidden = !text;
    syncEl.textContent = text || "";
    syncEl.className = "abp__sync" + (tone ? " is-" + tone : "");
  }

  /* The signed-out line carries a real sign-in link, so the offer to keep
     the record across devices is one tap, not an instruction. */
  function statusAnon() {
    if (!syncEl) return;
    syncEl.hidden = false;
    syncEl.className = "abp__sync is-anon";
    syncEl.textContent = "Saved on this device. ";
    const a = document.createElement("a");
    a.className = "abp__sync-link";
    a.href = LOGIN;
    a.textContent = "Sign in to keep your record on every device.";
    syncEl.appendChild(a);
  }

  let pushTimer = null;
  let pushing = false;
  let dirty = false;

  function push() {
    if (!CUSTOMER) return;
    clearTimeout(pushTimer);
    pushTimer = setTimeout(send, 700);
  }

  async function send() {
    if (!CUSTOMER) return;
    /* A save is already in flight. Remember that the record moved again
       rather than dropping the change — two quick taps must both land. */
    if (pushing) { dirty = true; return; }
    pushing = true;
    status("Saving to your account…", "busy");
    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ progress: state })
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      status("Saved to your account", "ok");
    } catch (e) {
      /* Saved on this device regardless — say so plainly rather than
         implying the work was lost. */
      status("Saved on this device. We could not reach your account just now.", "warn");
    } finally {
      pushing = false;
      if (dirty) { dirty = false; push(); }
    }
  }

  async function pull() {
    if (!CUSTOMER) { statusAnon(); return; }
    status("Checking your account…", "busy");
    try {
      const res = await fetch(ENDPOINT, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const data = await res.json();
      const remote = (data && data.progress) || {};
      const merged = merge(state, remote);
      const changed = JSON.stringify(merged) !== JSON.stringify(state);
      state = merged;
      try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) {}
      paintAll();
      const open = q("[data-abp-program]:not([hidden])");
      if (open) paintDays(open);
      if (changed) send(); else status("Saved to your account", "ok");
    } catch (e) {
      status("Saved on this device. We could not reach your account just now.", "warn");
    }
  }

  /* --------------------------------------------------------------- paint */
  function paintDays(panel) {
    const handle = panel.dataset.abpProgram;
    const rec = state[handle] || {};
    qa("[data-abp-day]", panel).forEach((day) => {
      const i = day.dataset.abpDay;
      const on = Boolean(rec[i]);
      day.classList.toggle("is-done", on);
      const btn = q(".abp__done", day);
      if (btn) {
        btn.setAttribute("aria-pressed", on ? "true" : "false");
        const label = q(".abp__done-label", btn);
        if (label) label.textContent = on ? "Done" : "Mark done";
      }
      const stamp = q(".abp__stamp", day);
      if (stamp) stamp.textContent = on ? "Finished " + fmt(rec[i]) : "";
    });
    paintTally(panel);
  }

  function paintTally(panel) {
    if (!tally) return;
    const handle = panel && panel.dataset.abpProgram;
    if (!handle) { tally.hidden = true; return; }
    const total = qa("[data-abp-day]", panel).length;
    const done  = doneCount(handle);
    if (!done) { tally.hidden = true; return; }
    tally.hidden = false;
    q("[data-abp-n]", tally).textContent = done;
    q("[data-abp-of]", tally).textContent = "of " + total + " days finished";
    const pips = q("[data-abp-pips]", tally);
    pips.innerHTML = "";
    const rec = state[handle] || {};
    for (let i = 1; i <= total; i++) {
      const s = document.createElement("span");
      s.className = "abp__pip" + (rec[i] ? " is-on" : "");
      pips.appendChild(s);
    }
  }

  /* The record: every program he has touched, newest work first. This is
     the "see my progress" view, and it reads the same signed in or out. */
  function paintRecord() {
    if (!record || !recList) return;
    const rows = [];
    cards().forEach((card) => {
      const handle = card.dataset.abpCard;
      const rec = state[handle] || {};
      const days = Object.keys(rec);
      if (!days.length) return;
      const total = parseInt(card.dataset.abpTotal, 10) || days.length;
      let last = 0;
      days.forEach((d) => { const t = Date.parse(rec[d]); if (t > last) last = t; });
      rows.push({
        handle: handle,
        name: card.dataset.abpName || handle,
        done: days.length,
        total: total,
        last: last,
        rec: rec
      });
    });

    if (!rows.length) { record.hidden = true; recList.innerHTML = ""; return; }
    rows.sort((a, b) => b.last - a.last);
    record.hidden = false;
    recList.innerHTML = "";

    rows.forEach((r) => {
      const li = document.createElement("li");
      li.className = "abp__rec" + (r.done >= r.total ? " is-complete" : "");

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "abp__rec-btn";
      btn.dataset.abpCard = r.handle;

      const name = document.createElement("span");
      name.className = "abp__rec-name";
      name.textContent = r.name;

      const pips = document.createElement("span");
      pips.className = "abp__pips";
      for (let i = 1; i <= r.total; i++) {
        const s = document.createElement("span");
        s.className = "abp__pip" + (r.rec[i] ? " is-on" : "");
        pips.appendChild(s);
      }

      const count = document.createElement("span");
      count.className = "abp__rec-count";
      count.textContent = r.done >= r.total
        ? "All " + r.total + " days finished"
        : r.done + " of " + r.total + " days";

      const when = document.createElement("span");
      when.className = "abp__rec-when";
      when.textContent = r.last ? "Last " + fmt(new Date(r.last).toISOString()) : "";

      btn.appendChild(name);
      btn.appendChild(pips);
      btn.appendChild(count);
      btn.appendChild(when);
      li.appendChild(btn);
      recList.appendChild(li);
    });
  }

  function paintAll() {
    qa("[data-abp-program]").forEach((p) => {
      if (!p.hidden) paintDays(p);
    });
    cards().forEach((card) => {
      card.classList.toggle("is-done", doneCount(card.dataset.abpCard) > 0);
    });
    paintRecord();
  }

  /* ------------------------------------------------------------ routing */
  function openProgram(handle, animate) {
    const panel = q('[data-abp-program="' + handle + '"]');
    if (!panel) return;
    qa("[data-abp-program]").forEach((p) => { p.hidden = p !== panel; });
    grid.hidden = true;
    if (record) record.hidden = true;
    detail.hidden = false;
    paintDays(panel);
    if (animate !== false && history.replaceState) {
      history.replaceState(null, "", "#p-" + handle);
    }
    const top = root.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top: top, behavior: animate === false ? "auto" : "smooth" });
    const h = q(".abp__title", panel);
    if (h) h.setAttribute("tabindex", "-1"), h.focus({ preventScroll: true });
  }

  function showGrid() {
    detail.hidden = true;
    grid.hidden = false;
    if (tally) tally.hidden = true;
    paintRecord();
    if (history.replaceState) history.replaceState(null, "", location.pathname);
    const top = root.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top: top, behavior: "smooth" });
  }

  /* ------------------------------------------------------------- events */
  root.addEventListener("click", (e) => {
    const card = e.target.closest("[data-abp-card]");
    if (card) { openProgram(card.dataset.abpCard); return; }

    if (e.target.closest("[data-abp-back]")) { showGrid(); return; }

    const btn = e.target.closest(".abp__done");
    if (btn) {
      const day    = btn.closest("[data-abp-day]");
      const panel  = btn.closest("[data-abp-program]");
      const handle = panel.dataset.abpProgram;
      const i = day.dataset.abpDay;
      state[handle] = state[handle] || {};
      if (state[handle][i]) {
        delete state[handle][i];
        if (!Object.keys(state[handle]).length) delete state[handle];
      } else {
        state[handle][i] = new Date().toISOString();
      }
      save(state);
      paintDays(panel);
      const c = q('[data-abp-grid] [data-abp-card="' + handle + '"]');
      if (c) c.classList.toggle("is-done", doneCount(handle) > 0);
      return;
    }

    if (e.target.closest("[data-abp-reset]")) {
      const panel = q("[data-abp-program]:not([hidden])");
      if (!panel) return;
      const handle = panel.dataset.abpProgram;
      delete state[handle];
      save(state);
      paintDays(panel);
      const c = q('[data-abp-grid] [data-abp-card="' + handle + '"]');
      if (c) c.classList.remove("is-done");
    }
  });

  /* deep link straight to a program */
  const hash = location.hash.match(/^#p-(.+)$/);
  if (hash) openProgram(decodeURIComponent(hash[1]), false);

  paintAll();
  pull();

  /* Another tab on the same device marked a day. Keep them in step. */
  window.addEventListener("storage", (e) => {
    if (e.key !== KEY) return;
    state = load();
    paintAll();
    const open = q("[data-abp-program]:not([hidden])");
    if (open) paintDays(open);
  });
})();
